import React, { useState, useEffect, useCallback } from 'react';
import { cpGetAuthHeader } from './ClientPortalApp';
import { openFilePreview } from '../common/FilePreviewModal';

const API = '/api/client/permit-applications/';

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '-');

const ClientPermitApplications = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQ) params.append('q', searchQ);
            const res = await fetch(`${API}?${params.toString()}`, { headers: cpGetAuthHeader() });
            const data = await res.json();
            setJobs(data.results || data || []);
        } catch {
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [searchQ]);

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchData();
    };

    const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

    return (
        <>
            <div className="cp-page-header">
                <h2>Permit Applications</h2>
            </div>

            <div className="cp-card">
                <form onSubmit={handleSearch}>
                    <div className="cp-filters">
                        <div className="cp-filter-group">
                            <label>Search (KK No, IDF, TANSAD)</label>
                            <input
                                type="text"
                                placeholder="Search…"
                                value={searchQ}
                                onChange={e => setSearchQ(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="cp-btn cp-btn-primary" style={{ alignSelf: 'flex-end' }}>
                            <i className="fa-solid fa-search"></i> Search
                        </button>
                    </div>
                </form>
            </div>

            {loading ? (
                <div className="cp-loader"><i className="fa-solid fa-circle-notch fa-spin"></i> Loading…</div>
            ) : jobs.length === 0 ? (
                <div className="cp-empty">No permit applications found.</div>
            ) : (
                <div className="cp-table-wrap">
                    <table className="cp-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>KK File No</th>
                                <th>IDF No</th>
                                <th>TANSAD No</th>
                                <th>UCR No</th>
                                <th>Invoice No</th>
                                <th>Permits</th>
                                <th>Documents</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map(job => (
                                <React.Fragment key={job.id}>
                                    <tr>
                                        <td>
                                            <button
                                                className="cp-btn-icon"
                                                title={expandedId === job.id ? 'Collapse' : 'Expand'}
                                                onClick={() => toggleExpand(job.id)}
                                            >
                                                <i className={`fa-solid ${expandedId === job.id ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                            </button>
                                        </td>
                                        <td>{job.kk_file_no || '-'}</td>
                                        <td>{job.idf_no || '-'}</td>
                                        <td>{job.tansad_no || '-'}</td>
                                        <td>{job.ucr_no || '-'}</td>
                                        <td>{job.invoice_no || '-'}</td>
                                        <td>
                                            {(job.manual_permits || []).length > 0 ? (
                                                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {job.manual_permits.map((p, i) => (
                                                        <span key={i} className="cp-badge cp-badge-orange">
                                                            {typeof p === 'object' ? (p.permit || p.type || JSON.stringify(p)) : p}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="cp-badge">None</span>
                                            )}
                                        </td>
                                        <td>
                                            {(job.documents || []).length > 0
                                                ? `${job.documents.length} file(s)`
                                                : '—'}
                                        </td>
                                        <td>{formatDate(job.created_at)}</td>
                                    </tr>

                                    {expandedId === job.id && (
                                        <tr>
                                            <td colSpan={9} style={{ background: '#f8fafc', padding: '12px 24px' }}>
                                                {/* Permit entries */}
                                                {(job.manual_permits || []).length > 0 && (
                                                    <>
                                                        <div className="cp-section-title" style={{ marginTop: 0 }}>Permit Entries</div>
                                                        <div className="cp-table-wrap" style={{ marginBottom: 16 }}>
                                                            <table className="cp-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Permit Type</th>
                                                                        <th>Permit No</th>
                                                                        <th>Issue Date</th>
                                                                        <th>Expiry Date</th>
                                                                        <th>Status</th>
                                                                        <th>Remarks</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {job.manual_permits.map((p, i) => {
                                                                        const pObj = typeof p === 'object' ? p : { permit: p };
                                                                        return (
                                                                            <tr key={i}>
                                                                                <td>{pObj.permit || pObj.type || '-'}</td>
                                                                                <td>{pObj.permit_no || pObj.number || '-'}</td>
                                                                                <td>{pObj.issue_date ? formatDate(pObj.issue_date) : '-'}</td>
                                                                                <td>{pObj.expiry_date ? formatDate(pObj.expiry_date) : '-'}</td>
                                                                                <td>
                                                                                    {pObj.status && (
                                                                                        <span className={`cp-badge cp-badge-${pObj.status === 'APPROVED' ? 'green' : 'orange'}`}>
                                                                                            {pObj.status}
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td>{pObj.remarks || '-'}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </>
                                                )}

                                                {/* Documents */}
                                                {(job.documents || []).length > 0 ? (
                                                    <>
                                                        <div className="cp-section-title">Attachments</div>
                                                        <div className="cp-table-wrap">
                                                            <table className="cp-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Document Type</th>
                                                                        <th>Doc No</th>
                                                                        <th>Issue Date</th>
                                                                        <th>Expiry Date</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {job.documents.map(doc => (
                                                                        <tr key={doc.id}>
                                                                            <td>{doc.document_type || '-'}</td>
                                                                            <td>{doc.document_no || '-'}</td>
                                                                            <td>{formatDate(doc.issue_date)}</td>
                                                                            <td>{formatDate(doc.expiry_date)}</td>
                                                                            <td>
                                                                                {doc.file_url ? (
                                                                                    <>
                                                                                        <button
                                                                                            className="cp-btn-icon"
                                                                                            title="View"
                                                                                            onClick={() => openFilePreview(doc.file_url, doc.document_type || 'Document')}
                                                                                        >
                                                                                            <i className="fa-solid fa-eye"></i>
                                                                                        </button>
                                                                                        <a
                                                                                            href={doc.file_url}
                                                                                            download
                                                                                            className="cp-btn-icon"
                                                                                            title="Download"
                                                                                            style={{ display: 'inline-flex', alignItems: 'center' }}
                                                                                        >
                                                                                            <i className="fa-solid fa-download"></i>
                                                                                        </a>
                                                                                    </>
                                                                                ) : '—'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="cp-empty" style={{ padding: '12px 0' }}>No attachments found.</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default ClientPermitApplications;
